import React, { useEffect, useRef, useState } from "react";
import { Item, ComboBox } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListJournalRequest } from "../../../../../../services/soap/finance/requests/ListJournalRequest";

interface journal {
  id: string,
  name: string,
  isPrimary: string,
  type: string,
  numbering: string,
  numberingName: string,
  treeHandle?: string,
  treehandle?: string,
  accountBase?: string,
  accountBaseName?: string,
  accountRound?: string,
  accountRoundName?: string,
  accountTotal?: string,
  accountTotalName?: string,
  accountVAT?: string,
  accountVATName?: string,
}

interface IJournalDropDownProps {
  selectedJournal: string;              // parent selected id
  selectedJournalName?: string;
  onChange: (data: any | null) => void; // null means cleared
  isEdit: any;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
  journalType?: string;
}

const JournalDropDown: React.FC<IJournalDropDownProps> = ({
  selectedJournal,
  selectedJournalName,
  onChange,
  isEdit,
  isDisabled,
  isReadOnly,
  isRequired,
  errorMessage,
  validationState,
  journalType
}) => {
  const { store, financeService } = useDependency();
  const { t } = useTranslation("invoice");
  const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const didInitialAutoSelectRef = useRef(false);
  const isUserEditingRef = useRef(false);

  const list = useAsyncList<journal>({
    async load({ filterText }) {
      try {
        const request = new ListJournalRequest(store.Server, store.SessionId);
        const response = await financeService.listJournal(request);

        let items: journal[] = response?.JOURNAL || [];
        if (!Array.isArray(items)) return { items: [] };

        if (journalType) {
          items = items.filter((x) => String(x.type ?? "") === String(journalType));
        }

        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((x) => (x.name ?? "").toLowerCase().includes(ft));
        }

        return { items };
      } catch (e) {
        console.error("Error fetching ItemStatus:", e);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPreferredDefaultJournal = (items: journal[]): journal | undefined => {
    if (!Array.isArray(items) || items.length === 0) return undefined;
    const primary = items.find((x) => String(x?.isPrimary ?? "") === "1");
    return primary || items[0];
  };

  // Initial load: auto-select preferred journal once in Add mode.
  useEffect(() => {
    if (didInitialAutoSelectRef.current) return;
    if (isEdit) return;
    if (selectedJournal) return;
    if (selectedKey) return;
    if (!list.items.length) return;

    const preferred = getPreferredDefaultJournal(list.items as journal[]);
    if (!preferred?.id) return;

    didInitialAutoSelectRef.current = true;

    const preferredId = String(preferred.id);
    setSelectedKey(preferredId);
    setInputValue(preferred.name ?? "");
    onChange(preferred);
  }, [list.items, isEdit, onChange, selectedJournal, selectedKey]);

  // Sync from parent only when user is not actively typing.
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedJournal) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    const selected = list.items.find((x) => String((x as any).id) === String(selectedJournal));
    setSelectedKey(selected ? String(selectedJournal) : null);
    setInputValue(selected?.name ?? selectedJournalName ?? "");
  }, [selectedJournal, selectedJournalName, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    // Allow deleting text even if something is selected.
    // If user input diverges from selected option label, clear the selectedKey.
    if (selectedKey) {
      const selected = list.items.find((x) => String((x as any).id) === String(selectedKey));
      const selectedLabel = (selected?.name ?? "");
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    // When cleared, reset selection.
    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText(""); // reset filtering
      onChange(null);         // tell parent it is cleared
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String((x as any).id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? ""); // show immediately
      onChange(selected);

      // clear filter so next focus shows all values
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={_t("journal")}
      aria-label={_t("journal")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      width={{ base: "100%", L: "100%" }}
      isDisabled={!!isDisabled}
      isReadOnly={!!isReadOnly}
      isRequired={isRequired}
      errorMessage={errorMessage}
      validationState={validationState}
      onFocus={() => {
        // Show all values when user clicks in.
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={(item as any).id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default JournalDropDown;
